import React from 'react';

import { useIntl } from 'react-intl';

import CartButton from 'components/app-header/mobile-header/cart-button';
import { HamburgerButton } from 'components/app-header/mobile-header/hamburger-button';
import {
  Header,
  LinkNoDecoration,
  MenuButtonContainer,
  MobileHeaderContainer,
} from 'components/app-header/styled';
import {
  NO_NETWORK_BACKGROUND_COLOR,
  NoNetworkHeader,
} from 'components/error-network/no-network-header';
import { LayoutContainer } from 'components/layout/styled';
import Logo from 'components/logo';
import { TopServiceMode } from 'components/top-service-mode';
import { useIsTopServiceModeEnabled } from 'experiments/service-mode/use-is-top-servie-mode-enabled';
import { useRoute } from 'hooks/navigation/use-route';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingIndicator } from 'state/loading';
import { useMobileHeaderBackButton, useMobileHeaderComponent } from 'state/mobile-header-nav';
import { useNetworkContext } from 'state/network';
import { useReversedUIContext } from 'state/reversed-ui';
import { theme } from 'styles/configure-theme';
import { routes } from 'utils/routing';

import SignupMobileHeader from '../signup-mobile-header';
import {
  HeaderContainer,
  StyledCenterSpacer,
  StyledLogo,
  StyledRightSpacer,
  TopServiceModeContainer,
} from '../styled';

import MobileHeaderCta from './mobile-header-cta';

const StandardMobileHeader = () => {
  const BackButtonComponent = useMobileHeaderBackButton();
  const { isNetworkConnected, isInternetReachable } = useNetworkContext();
  const { pathname } = useRoute();

  const LoadingIndicator = useLoadingIndicator();
  const { formatMessage } = useIntl();
  const enableTopServiceMode = useIsTopServiceModeEnabled();
  const { reversedUI } = useReversedUIContext();

  const isHamburgerMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_HAMBURGER_MENU_ON_MOBILE);

  const isMobile = useIsMobileBreakpoint();

  const shouldShowHamburgerMenu =
    pathname.includes(formatMessage({ id: 'routes.termsConditionsRewards' })) ||
    pathname.includes(formatMessage({ id: 'routes.rewardsPolicy' })) ||
    pathname.includes(formatMessage({ id: 'routes.deliveryTerms' })) ||
    pathname.includes(formatMessage({ id: 'routes.termsOfService' })) ||
    pathname.includes(formatMessage({ id: 'routes.privacyPolicy' }));

  const { isInAuthFlow } = useAuthFlow();
  if (isInAuthFlow) {
    return <SignupMobileHeader />;
  }

  const LeftSideComponent =
    (!shouldShowHamburgerMenu && BackButtonComponent) ||
    (isHamburgerMenuEnabled ? HamburgerButton : () => null);

  const normalBackground = reversedUI
    ? theme.token('background-hero-reversed')
    : theme.token('background-hero-light');

  const showNoNetworkHeader = !isNetworkConnected || !isInternetReachable;

  return (
    <MobileHeaderContainer
      accessibilityLabel={formatMessage({ id: 'primaryNavLinks' })}
      testID="mobile-header"
      background={showNoNetworkHeader && isMobile ? NO_NETWORK_BACKGROUND_COLOR : normalBackground}
    >
      {showNoNetworkHeader && <NoNetworkHeader />}
      <LayoutContainer background={normalBackground}>
        <HeaderContainer reversed={reversedUI}>
          <Header>
            <MenuButtonContainer>
              <LeftSideComponent />
            </MenuButtonContainer>
            <StyledCenterSpacer>
              <LinkNoDecoration to={routes.base}>
                <StyledLogo testID="mobile-header-logo">
                  <Logo padding="0" />
                </StyledLogo>
              </LinkNoDecoration>
            </StyledCenterSpacer>
            <StyledRightSpacer>
              <MobileHeaderCta />
              <CartButton />
            </StyledRightSpacer>
          </Header>
          <LoadingIndicator />
          {enableTopServiceMode && (
            <TopServiceModeContainer>
              <TopServiceMode />
            </TopServiceModeContainer>
          )}
        </HeaderContainer>
      </LayoutContainer>
    </MobileHeaderContainer>
  );
};

export default function MobileHeader() {
  const HeaderComponent = useMobileHeaderComponent();

  return HeaderComponent ? <HeaderComponent /> : <StandardMobileHeader />;
}
